import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Flag from "../../components/Flag";
import LoadingContent from "../../components/LoadingContent";
import ErrorPage from "../ErrorPage";
import "../../assets/style/driver.css";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {
  faCalculator,
  faTachometer,
  faFlagCheckered,
  faMedal,
  faRoad,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import Stat from "../../components/Stat";
import DriverResults from "./parts/DriverResults";
import EventResults from "./parts/EventResults";
import Counter from "../../components/Counter";

const Driver = () => {
  let { username } = useParams();
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const [maxRacesCir, setMaxRacesCir] = useState(0);
  const [maxRacesHC, setMaxRacesHC] = useState(0);
  const [fastestLaps, setFastestLaps] = useState(0);
  const [wins, setWins] = useState(0);
  const [poles, setPoles] = useState(0);
  const [podiums, setPodiums] = useState(0);
  const [points, setPoints] = useState(0);
  const [races, setRaces] = useState(0);
  const [finalPos, setFinalPos] = useState(0);
  const [totalChamps, setTotalChamps] = useState(0);
  const [titles, setTitles] = useState(0);
  const [chartData, setChartData] = useState<any>([]);
  const [user, setUser] = useState<User>();
  const [summary, setSummary] = useState<DriverSummary[]>([]);
  const [events, setEvents] = useState<EventResults[]>([]);
  document.title = "LVF1 - " + username;

  useEffect(() => {
    if (username != null)
      axios
        .get(
          `${
            process.env.NODE_ENV === "development"
              ? "http://localhost:5500"
              : "https://ligavirtualf1.es:5500"
          }/api/driver/getDriverResults`,
          {
            params: { username },
          },
        )
        .then(({ data }) => {
          console.log(data);
          setUser(data.user);
          setSummary(data.summary);
          setMaxRacesCir(data.max_racescir);
          setMaxRacesHC(data.max_raceshc);
          setEvents(data.event_results);

          const sumarPropiedad = (data: any, key: string) => {
            return data.reduce((acumulador: number, item: any) => {
              // Usamos el operador de coalescencia nula (??)
              // para tratar null/undefined como 0 directamente
              const valor = Number(item[key]) ?? 0;
              return acumulador + (isNaN(valor) ? 0 : valor);
            }, 0);
          };
          setFastestLaps(sumarPropiedad(data.summary, "fastest_laps"));
          setWins(sumarPropiedad(data.summary, "wins"));
          setPoles(sumarPropiedad(data.summary, "poles"));
          setPodiums(sumarPropiedad(data.summary, "podiums"));
          setRaces(sumarPropiedad(data.summary, "gps"));
          setPoints(sumarPropiedad(data.summary, "points"));

          const mejorPosicion = data.summary.reduce(
            (mejor: number, item: any) => {
              const pos = Number(item.position);
              // Solo consideramos posiciones válidas (mayores a 0)
              if (pos > 0) {
                return mejor === 0 ? pos : Math.min(mejor, pos);
              }
              return mejor;
            },
            0,
          );
          setFinalPos(mejorPosicion);
          const totalTitles = data.summary.reduce(
            (contador: number, item: any) => {
              return Number(item.position) === 1 ? contador + 1 : contador;
            },
            0,
          );
          setTitles(totalTitles);
          const total =
            data.event_results.length > 0
              ? data.summary.length - 1
              : data.summary.length;
          setTotalChamps(total);
          setChartData([...data.last_results].reverse());
        })
        .catch(({ response }) => {
          setError(true);
          throw new Error(response);
        });
  }, [username]);

  useEffect(() => {
    // 1. Verificación de seguridad
    if (!chartData || chartData.length === 0) return;

    // 2. Crear root
    let root = am5.Root.new("chartdiv");
    root.setThemes([am5themes_Animated.new(root)]);

    // 3. Crear el gráfico
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "none",
        wheelY: "none",
      }),
    );

    // 4. Cursor (OBLIGATORIO para tooltips)
    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "none",
      }),
    );

    // 5. Eje X
    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "name",
        renderer: am5xy.AxisRendererX.new(root, {
          visible: false,
        }),
      }),
    );
    xAxis.data.setAll(chartData);
    xAxis.get("renderer").grid.template.set("visible", false);

    // Ocultamos las etiquetas de texto del eje X
    xAxis.get("renderer").labels.template.set("visible", false);
    // 6. Eje Y
    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          inversed: true,
          minGridDistance: 20,
        }),
        min: 0.1,
        max: 25,
        strictMinMax: true,
      }),
    );
    yAxis.get("renderer").grid.template.setAll({
      stroke: am5.color(
        window.localStorage.getItem("theme") === "dark" ? 0x2b2b2b : 0xb7b7b7,
      ), // Cambia a tu color preferido (ej. gris claro)
      strokeOpacity: 0.3, // Opacidad para que no sea muy intrusivo
      strokeWidth: 1,
    });
    // Desactivamos las etiquetas automáticas
    yAxis.get("renderer").labels.template.set("visible", false);
    const labelsToForce = [1, 5, 10, 15, 20];

    labelsToForce.forEach((val) => {
      let rangeDataItem = yAxis.makeDataItem({ value: val });
      let range = yAxis.createAxisRange(rangeDataItem);

      range.get("label")?.setAll({
        visible: true,
        text: val.toString(),
        fill: am5.color(
          window.localStorage.getItem("theme") === "dark" ? 0xffffff : 0x000000,
        ),
        fontSize: 12,
      });

      // Opcional: añadir una línea guía tenue para esos valores
      range.get("grid")?.setAll({
        strokeOpacity: 0.2,
        strokeDasharray: [3, 3],
      });
    });
    const defaultColour =
      window.localStorage.getItem("theme") === "dark" ? "#FFFFFF" : "#000000";
    const turquoise =
      window.localStorage.getItem("theme") === "dark" ? "#3bd8c9" : "#179387";
    yAxis.get("renderer").labels.template.setAll({
      fontSize: 10, // Tamaño de letra más pequeño
      fill: am5.color(defaultColour), // Color gris opcional para que no distraiga
    });
    // 7. Serie de líneas
    let series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: "Posición",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "position",
        categoryXField: "name",
        stroke: am5.color(0x40e0d0),
        fill: am5.color(0x40e0d0),
      }),
    );

    // 8. Bullets (puntos) con interactividad
    series.bullets.push((root, series, dataItem) => {
      // Accedemos a los datos de este punto específico
      const data: any = dataItem.dataContext;
      const isRetired = data.isRet;
      const typeRaces = data.type_races;

      // Definimos las propiedades según el estado
      const sprite = isRetired
        ? am5.Rectangle.new(root, {
            // Si está retirado: Cuadrado (X)
            width: 8,
            height: 8,
            dx: -4,
            dy: -4,
            fill: am5.color(0x9f38dd), // Rojo para retirado
            tooltipText: `{name}: [bold]DNF[/]`,
          })
        : am5.Circle.new(root, {
            // Si está en carrera: Círculo (Turquesa)
            radius: 4,
            fill: am5.color(typeRaces === 0 ? turquoise : 0xd86d3b),
            tooltipText: "{name}: [bold]{valueY}º[/]",
          });

      return am5.Bullet.new(root, {
        sprite: sprite,
      });
    });

    series.data.setAll(chartData);

    // 9. Limpieza al desmontar el componente
    return () => {
      root.dispose();
    };
  }, [chartData]);

  if (error || username == null) return <ErrorPage />;
  if (user == null) return <LoadingContent />;
  return (
    <>
      <div className="full-height padding-md app-container  page-transition">
        <div className="driver-card general-info">
          <h5 className="h3-driver-name">
            <Flag flag_code={user.flag_code} name={user.flag_name} />
            {username}
          </h5>
          <div className="flex">
            <div>
              <div id="photo-summary-container" className="flex">
                <div>
                  <img
                    className="driver-photo"
                    src={
                      user.hasImage != null && user.hasImage
                        ? `/assets/Drivers/${user.id}.png?dummy=${Math.floor(
                            Math.random() * 1001,
                          )}`
                        : require(`../../assets/Images/NoPhoto.png`)
                    }
                    alt={username}
                  />
                </div>
              </div>
            </div>
            <div className="row-stats-parent">
              <div className="row-stats">
                <label className="driver-title">
                  {t("CareerSummary").toUpperCase()}
                </label>
                <div className="flex">
                  <Stat
                    icon={faTrophy}
                    val={wins}
                    text={t("Wins").toUpperCase()}
                  />
                  <Stat
                    icon={faFlagCheckered}
                    val={poles}
                    text={t("Poles").toUpperCase()}
                  />
                  <Stat
                    icon={faTachometer}
                    val={fastestLaps}
                    text={t("HotLaps").toUpperCase()}
                  />
                  <Stat
                    icon={faMedal}
                    val={podiums}
                    text={t("Podiums").toUpperCase()}
                  />
                  <Stat
                    icon={faRoad}
                    val={races}
                    text={t("Races").toUpperCase()}
                  />
                  <Stat
                    icon={faCalculator}
                    val={points}
                    text={t("Points").toUpperCase()}
                  />
                </div>
                <div className="flex">
                  <div className="flex longstat">
                    <label className="subt">
                      {t("BestFinalPosition").toUpperCase()}
                    </label>
                    <div className="finalpos-container">
                      <label
                        className={`value finalpos ${finalPos < 4 ? `p${finalPos}` : ""}`}
                      >
                        {finalPos}º
                      </label>
                    </div>
                  </div>
                  <div className="flex longstat">
                    <label className="subt">{t("Titles").toUpperCase()}</label>
                    <Counter targetValue={titles.toString()} />
                  </div>
                  <div className="flex longstat">
                    <label className="subt">{t("Seasons").toUpperCase()}</label>
                    <Counter targetValue={totalChamps.toString()} />
                  </div>
                </div>
              </div>
            </div>
            {chartData.length > 0 && (
              <div className="stats-chart-container">
                <label className="driver-title">
                  {t("LastResults").toUpperCase()}
                </label>
                <div
                  id="chartdiv"
                  style={{ width: "100%", height: "165px" }}
                ></div>
              </div>
            )}
          </div>
        </div>
        {summary.filter((s: any) => s.type_races === 0)?.length > 0 && (
          <div className="driver-card f1-results">
            <label className="driver-title">
              {t("CircuitRacing").toUpperCase()}
            </label>
            <DriverResults
              summary={summary}
              maxRaces={maxRacesCir}
              driver={username}
              type={0}
            />
          </div>
        )}

        {summary.filter((s: any) => s.type_races === 1)?.length > 0 && (
          <div className="driver-card hc-results">
            <label className="driver-title hc">
              {t("Rally / HillClimb").toUpperCase()}
            </label>
            <DriverResults
              summary={summary}
              maxRaces={maxRacesHC}
              driver={username}
              type={1}
            />
          </div>
        )}

        {events.length != 0 && (
          <div className="driver-card event-results">
            <label className="driver-title ev">
              {t("Events").toUpperCase()}
            </label>
            <EventResults events={events} />
          </div>
        )}
        {/*<div className="full-height padding-md app-container  page-transition">
          <SummaryTable summary={summary} />
        </div>
        <h3 className="results-title">{t("Results")}</h3>
        <DriverResults
          summary={summary}
          maxRaces={maxRaces}
          driver={username}
        />
        {events.length != 0 && (
          <>
            <h3 className="results-title">{t("EventsResults")}</h3>
            <EventResults events={events} />
          </>
        )}*/}
      </div>
    </>
  );
};

export default Driver;
